import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiMessageSquare, FiUsers, FiSearch, FiPlus, FiThumbsUp, FiMessageCircle, 
  FiFilter, FiTrendingUp, FiClock, FiBookmark, FiMoreHorizontal, FiEdit3, 
  FiTrash2, FiShare2, FiEye, FiAward, FiCalendar, FiTag, FiSend, FiPaperclip,
  FiHeart, FiUser, FiMapPin, FiGlobe, FiSettings, FiBell, FiX
} = FiIcons;

const CommunityPortal = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newPostContent, setNewPostContent] = useState('');
  const [showNewPost, setShowNewPost] = useState(false);
  const [posts, setPosts] = useState([
    {
      id: 1,
      title: 'Best practices for managing complex implant cases',
      content: 'I\'ve been working on a particularly challenging implant case involving a patient with severe bone loss. The patient requires multiple sinus lifts and bone grafts before we can even begin the implant process...',
      author: {
        name: 'Dr. Sarah Mitchell',
        avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face',
        role: 'Oral Surgeon',
        practice: 'Downtown Dental Care',
        location: 'New York, NY',
        points: 2847
      },
      category: 'clinical',
      likes: 24,
      comments: 8,
      views: 156,
      timestamp: '2 hours ago',
      isPinned: true,
      tags: ['implants', 'oral-surgery', 'bone-grafting'],
      liked: false,
      bookmarked: false
    },
    {
      id: 2,
      title: 'New intraoral scanner recommendations',
      content: 'Looking to upgrade our practice\'s intraoral scanning technology. Currently using an older model that\'s been reliable but lacks some of the newer features...',
      author: {
        name: 'Dr. Michael Chen',
        avatar: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=40&h=40&fit=crop&crop=face',
        role: 'General Dentist',
        practice: 'Family Dental Care',
        location: 'San Francisco, CA',
        points: 2156
      },
      category: 'technology',
      likes: 18,
      comments: 12,
      views: 89,
      timestamp: '4 hours ago',
      tags: ['technology', 'equipment', 'digital-dentistry'],
      liked: true,
      bookmarked: true
    },
    {
      id: 3,
      title: 'Patient communication strategies for treatment acceptance',
      content: 'I\'ve been struggling with treatment plan acceptance rates lately. Despite thorough explanations and visual aids, many patients seem hesitant to proceed with recommended treatments...',
      author: {
        name: 'Dr. Lisa Rodriguez',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
        role: 'Orthodontist',
        practice: 'Bright Smiles Dental',
        location: 'Miami, FL',
        points: 1893
      },
      category: 'practice',
      likes: 31,
      comments: 15,
      views: 203,
      timestamp: '6 hours ago',
      tags: ['patient-care', 'communication', 'practice-growth'],
      liked: false,
      bookmarked: false
    }
  ]);
  
  const [comments, setComments] = useState({
    1: [
      {
        id: 1,
        author: {
          name: 'Dr. James Wilson',
          avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5cd?w=40&h=40&fit=crop&crop=face'
        },
        content: 'Excellent point about pre-planning. I\'ve found that involving the patient in the 3D planning process significantly improves acceptance rates.',
        timestamp: '1 hour ago',
        likes: 3
      }
    ],
    2: [],
    3: [
      {
        id: 1,
        author: {
          name: 'Dr. Robert Davis',
          avatar: 'https://images.unsplash.com/photo-1566753323558-f4e0952af115?w=40&h=40&fit=crop&crop=face'
        },
        content: 'We\'ve had great success with video explanations combined with printed materials. Patients retain information better this way.',
        timestamp: '3 hours ago',
        likes: 5
      }
    ]
  });

  const categories = [
    { id: 'all', label: 'All Topics', icon: FiMessageSquare },
    { id: 'clinical', label: 'Clinical Cases', icon: FiUsers },
    { id: 'technology', label: 'Technology', icon: FiAward },
    { id: 'practice', label: 'Practice Management', icon: FiCalendar },
    { id: 'education', label: 'Education', icon: FiTag }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      clinical: 'bg-blue-100 text-blue-800',
      technology: 'bg-purple-100 text-purple-800',
      practice: 'bg-green-100 text-green-800',
      education: 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const handleLikePost = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, liked: !post.liked, likes: post.liked ? post.likes - 1 : post.likes + 1 } 
        : post
    ));
  };

  const handleBookmarkPost = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, bookmarked: !post.bookmarked } 
        : post
    ));
  };

  const handleAddComment = (postId, commentText) => {
    if (!commentText.trim()) return;
    
    const newComment = {
      id: Date.now(),
      author: {
        name: user.name,
        avatar: user.avatar
      },
      content: commentText,
      timestamp: 'Just now',
      likes: 0
    };
    
    setComments({
      ...comments,
      [postId]: [...(comments[postId] || []), newComment]
    });
    
    // Update post comment count
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, comments: post.comments + 1 } 
        : post
    ));
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.content.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmitPost = () => {
    if (!newPostContent.trim()) return;
    
    const newPost = {
      id: Date.now(),
      title: newPostContent.split('\n')[0] || 'New Discussion',
      content: newPostContent,
      author: {
        name: user.name,
        avatar: user.avatar,
        role: user.role === 'provider' ? 'Dentist' : user.role === 'admin' ? 'Administrator' : 'Member',
        practice: user.practice || 'Community Member',
        location: 'Online',
        points: user.points || 0
      },
      category: selectedCategory === 'all' ? 'clinical' : selectedCategory,
      likes: 0,
      comments: 0,
      views: 0,
      timestamp: 'Just now',
      isPinned: false,
      tags: [],
      liked: false,
      bookmarked: false
    };
    
    setPosts([newPost, ...posts]);
    setNewPostContent('');
    setShowNewPost(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Community Forums</h1>
              <p className="text-gray-600 mt-1">Connect, share knowledge, and grow with dental professionals.</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <button 
                onClick={() => setShowNewPost(!showNewPost)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>New Post</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* New Post Form */}
        {showNewPost && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Post</h3>
              <button 
                onClick={() => setShowNewPost(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="What would you like to discuss?"
              className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button className="p-2 text-gray-400 hover:text-blue-600">
                  <SafeIcon icon={FiPaperclip} className="w-5 h-5" />
                </button>
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                >
                  {categories.filter(cat => cat.id !== 'all').map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <button 
                onClick={handleSubmitPost}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiSend} className="w-4 h-4" />
                <span>Post</span>
              </button>
            </div>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">2,847</p>
                <p className="text-sm text-gray-600">Active Members</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">156</p>
                <p className="text-sm text-gray-600">Discussions Today</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">89%</p>
                <p className="text-sm text-gray-600">Response Rate</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-50 rounded-lg">
                <SafeIcon icon={FiAward} className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">24</p>
                <p className="text-sm text-gray-600">Expert Contributors</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Search and Filters */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="text" 
                      placeholder="Search discussions..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64" 
                    />
                  </div>
                  <select 
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    <option value="clinical">Clinical Cases</option>
                    <option value="technology">Technology</option>
                    <option value="practice">Practice Management</option>
                    <option value="education">Education</option>
                  </select>
                </div>
                <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                  <SafeIcon icon={FiFilter} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Category Tabs */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <div className="flex items-center space-x-6 overflow-x-auto">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                      selectedCategory === category.id 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={category.icon} className="w-4 h-4" />
                    <span className="text-sm font-medium">{category.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Posts */}
            <div className="space-y-4">
              {filteredPosts.map((post, index) => (
                <motion.div 
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  {post.isPinned && (
                    <div className="flex items-center space-x-2 mb-3">
                      <SafeIcon icon={FiAward} className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium text-yellow-600">Pinned Post</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <img 
                        src={post.author.avatar} 
                        alt={post.author.name} 
                        className="w-10 h-10 rounded-full object-cover" 
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{post.author.name}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(post.category)}`}>
                            {post.category}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                          <span>{post.author.role}</span>
                          <span>•</span>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiMapPin} className="w-3 h-3" />
                            <span>{post.author.location}</span>
                          </div>
                          <span>•</span>
                          <span>{post.timestamp}</span>
                        </div>
                      </div>
                    </div>
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <SafeIcon icon={FiMoreHorizontal} className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h2>
                  <p className="text-gray-600 mb-4">{post.content}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag) => (
                      <span 
                        key={tag} 
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-6">
                      <button 
                        onClick={() => handleLikePost(post.id)}
                        className={`flex items-center space-x-2 transition-colors ${
                          post.liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'
                        }`}
                      >
                        <SafeIcon icon={post.liked ? FiHeart : FiThumbsUp} className="w-4 h-4 fill-current" />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <button className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                        <span className="text-sm">{post.comments}</span>
                      </button>
                      <button className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                        <span className="text-sm">{post.views}</span>
                      </button>
                      <button className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiShare2} className="w-4 h-4" />
                        <span className="text-sm">Share</span>
                      </button>
                    </div>
                    <button 
                      onClick={() => handleBookmarkPost(post.id)}
                      className={`p-1 transition-colors ${
                        post.bookmarked ? 'text-blue-500' : 'text-gray-400 hover:text-blue-500'
                      }`}
                    >
                      <SafeIcon icon={post.bookmarked ? FiBookmark : FiBookmark} className="w-4 h-4 fill-current" />
                    </button>
                  </div>
                  
                  {/* Comments Section */}
                  {comments[post.id] && comments[post.id].length > 0 && (
                    <div className="mt-6 space-y-4">
                      {comments[post.id].map(comment => (
                        <div key={comment.id} className="flex items-start space-x-3">
                          <img 
                            src={comment.author.avatar} 
                            alt={comment.author.name} 
                            className="w-8 h-8 rounded-full object-cover" 
                          />
                          <div className="flex-1">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-sm text-gray-900">{comment.author.name}</span>
                                <span className="text-xs text-gray-500">{comment.timestamp}</span>
                              </div>
                              <p className="text-sm text-gray-700">{comment.content}</p>
                            </div>
                            <div className="flex items-center space-x-3 mt-2">
                              <button className="text-xs text-gray-500 hover:text-blue-600">
                                Like ({comment.likes})
                              </button>
                              <button className="text-xs text-gray-500 hover:text-blue-600">
                                Reply
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Comment */}
                  <div className="mt-4 flex items-center space-x-3">
                    <img 
                      src={user.avatar} 
                      alt={user.name} 
                      className="w-8 h-8 rounded-full object-cover" 
                    />
                    <div className="flex-1 flex items-center space-x-2">
                      <input 
                        type="text" 
                        placeholder="Add a comment..." 
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddComment(post.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button className="p-2 text-gray-400 hover:text-blue-600">
                        <SafeIcon icon={FiPaperclip} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Trending Topics */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Trending Topics</h3>
              <div className="space-y-3">
                {[
                  { topic: 'Digital Workflow', posts: 42 },
                  { topic: 'Implant Planning', posts: 38 },
                  { topic: 'Patient Communication', posts: 35 },
                  { topic: 'Practice Efficiency', posts: 28 },
                  { topic: 'New Materials', posts: 24 }
                ].map((trend, index) => (
                  <motion.div 
                    key={trend.topic}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-700">#{trend.topic}</span>
                    <span className="text-xs text-gray-500">{trend.posts} posts</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Top Contributors */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Contributors</h3>
              <div className="space-y-3">
                {[
                  { name: 'Dr. Sarah Mitchell', role: 'Oral Surgeon', points: 2847 },
                  { name: 'Dr. Michael Chen', role: 'General Dentist', points: 2156 },
                  { name: 'Dr. Lisa Rodriguez', role: 'Orthodontist', points: 1893 }
                ].map((contributor, index) => (
                  <motion.div 
                    key={contributor.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{contributor.name}</p>
                      <p className="text-xs text-gray-500">{contributor.role}</p>
                    </div>
                    <span className="text-sm font-medium text-gray-600">{contributor.points}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Upcoming Events */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
              <div className="space-y-3">
                {[
                  { title: 'Webinar: Digital Dentistry Trends', date: 'Jan 20, 2024', type: 'Online' },
                  { title: 'Clinical Workshop: Implant Planning', date: 'Jan 25, 2024', type: 'In-Person' },
                  { title: 'Practice Management Summit', date: 'Feb 2, 2024', type: 'Hybrid' }
                ].map((event, index) => (
                  <motion.div 
                    key={event.title}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 + index * 0.1 }}
                    className="p-3 border border-gray-200 rounded-lg"
                  >
                    <p className="text-sm font-medium text-gray-900">{event.title}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">{event.date}</span>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded-full">
                        {event.type}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPortal;